import UserService from '@/services/user'
import PartnerService from '@/services/partner'
import base64 from 'base-64'
import Vue from 'vue'

const defaultState = {
  partner: {},
  token: '',
  image: '',
  loggedIn: false,
  softLogin: false
}

function processToken (commit, token, partner) { 
  let data = getTokenData(token)

  if (typeof data !== 'object' || !data.customerNumber || data.customerNumber.length < 1) {
    return Promise.reject(UserService.state.INVALID_PARTNER_NUMBER)
  }

  commit('login', token)

  if (!partner) {
    return Promise.resolve()
  }

  commit('setPartner', { number: data.customerNumber })

  return loadPartner(commit, data.customerNumber)
}

function loadPartner (commit, number) {
  return PartnerService.get(number).then(partner => {
    commit('setPartner', partner.data)

    PartnerService.getImage(number).then(image => {
      // block images that are to big for local storage
      if (image.data && image.data.length < 3145728) {
        commit('setImage', image.data)
      } else {
        commit('setImage', '')
      }
    })
    
    return Promise.resolve()
  })
}

function getTokenData(token) {
  const base64Url = token.split('.')[1]
  const base64Content = base64Url.replace(/-/g, '+').replace(/_/g, '/')

  return JSON.parse(base64.decode(base64Content))
}

const actions = {
  login ({ commit, rootState }, { emailAddress, password }) {
    if (emailAddress.match(/^prd__/) && rootState.apiMode !== 'prd') {
      commit('setApiMode', 'prd', { root: true })
    }
    else if (emailAddress.match(/^stg__/) && rootState.apiMode !== 'stg') {
      commit('setApiMode', 'stg', { root: true })
    }
    else if (emailAddress.match(/^dev__/) && rootState.apiMode !== 'dev') {
      commit('setApiMode', 'dev', { root: true })
    }
    else if (emailAddress.match(/^loc__/) && rootState.apiMode !== 'loc') {
      commit('setApiMode', 'loc', { root: true })
    }

    return UserService.login(emailAddress, password).then(response => {
      
      if (!response.token) {
        return Promise.reject(UserService.state.INVALID_TOKEN)
      }
      
      return processToken(commit, response.token, true).then(() => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'kupo_login'
        })

        return Promise.resolve(response)
      })
    }).catch(error => {
      //commit('setApiMode', 'prd', {root: true})
      commit('canSubmitInvoice', false, { root: true })
      commit('canSubmitClaim', false, { root: true })

      return Promise.reject(error)
    })
  },
  loginWithPartner ({ commit }, { partnerNumber, birthDate, postalCode, softLogin = false }) {
    return UserService.loginWithPartner(partnerNumber, birthDate, postalCode).then(response => {
      if (!response.token) {
        return Promise.reject(UserService.state.INVALID_TOKEN)
      }

      commit('setSoftLogin', softLogin)

      return processToken(commit, response.token, true).then(() => {
        return Promise.resolve(response)
      })
    }).catch(error => {
      commit('canSubmitInvoice', false, { root: true })
      commit('canSubmitClaim', false, { root: true })

      return Promise.reject(error)
    })
  },
  logout ({ commit }) {
    //commit('setApiMode', 'prd', {root: true})

    commit('logout')
  },
  setToken ({ commit }, { token }) {
    return processToken(commit, token, true)
  },
  checkToken ({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!state.token) resolve()

      const data = getTokenData(state.token)

      if ('partner_nr' in data && !state.softLogin) {
        UserService.exchangeToken().then(result => {
          processToken(commit, result.token)
          resolve()
        })
      } else if ((data.exp * 1000) < Date.now()) {
        UserService.refreshToken().then(result => {
          processToken(commit, result.token)
          resolve()
        }).catch(() => reject())
      } else {
        resolve()
      }
    })
  },
  setPartner ({ commit }, { number }) {
    return loadPartner(commit, number)
  },
  updatePartner ({ commit, state }, partner) {
    return PartnerService.set(state.partner.number, partner).then(result => {
      commit('setPartner', partner)

      return Promise.resolve(result)
    })
  },
  async updateImage ({ commit }, image) {
    const result = await PartnerService.setImage(image)
    
    commit('setImage', image)

    return result
  }
}

const getters = {
  name (state) {
    return state.partner.first_name && state.partner.last_name ? state.partner.first_name + ' ' + state.partner.last_name : ''
  },
  salutationAndName (state) {
    return state.partner.salutation && state.partner.last_name ? state.partner.salutation + ' ' + state.partner.last_name : ''
  },
  email (state) {
    return state.partner.email || ''
  },
  number (state) {
    return state.partner.number || '0000000'
  },
  image (state) {
    return state.image
  },
  loggedIn (state, dgdfg, globalState) {
    return globalState.settings.login_available ? state.loggedIn : false
  },
  isSoftLogin (state) {
    return state.softLogin
  },
}

const mutations = {
  login (state, token) {
    state.loggedIn = true
    state.token = token
  },
  logout (state) {
    state.loggedIn = defaultState.loggedIn
    state.token = defaultState.token
    state.partner = defaultState.partner
    state.image = defaultState.image
    state.softLogin = defaultState.softLogin
  },
  setSoftLogin (state, to) {
    state.softLogin = to
  },
  setPartner (state, partner) {
    if (typeof partner === 'object' && Object.keys(partner).length > 0) {
      for (let key in partner) {
        Vue.set(state.partner, key, partner[key])
      }
    }
  },
  setImage (state, image) {
    state.image = image
  }
}

export const user = {
  namespaced: true,
  state: Object.assign({}, defaultState),
  actions,
  getters,
  mutations
}